import { Timeframe } from '@ankr.com/chains-list';

import { SecondaryTab, TabSize } from 'modules/common/components/SecondaryTab';
import { Tab, TabID } from 'modules/common/hooks/useTabs';
import { getLabelByTimeframe } from 'modules/chains/utils/getLabelByTimeframe';

import { useTimeframeTabsStyles } from './useTimeframeTabsStyles';

const timeframeValues = new Set<string | number>(Object.values(Timeframe));

const isTimeframe = (value: string | number): value is Timeframe =>
  timeframeValues.has(value);

export interface TimeframeTabsProps<T extends string | number = Timeframe> {
  className?: string;
  disabled?: boolean;
  getLabel?: (timeframe: TabID<T>) => string;
  size?: TabSize;
  tabClassName?: string;
  tabs: Tab<T>[];
  timeframe: T;
}

export const TimeframeTabs = <T extends string | number = Timeframe>({
  className,
  disabled,
  getLabel,
  size = TabSize.Small,
  tabClassName,
  tabs,
  timeframe,
}: TimeframeTabsProps<T>) => {
  const { classes, cx } = useTimeframeTabsStyles(size);

  const getTabLabel = (id: TabID<T>) => {
    if (isTimeframe(id)) {
      return getLabelByTimeframe(id);
    }

    return getLabel?.(id) ?? String(id);
  };

  return (
    <div className={cx(className, classes.root)}>
      {tabs.map(({ id, onSelect }) => (
        <SecondaryTab
          className={cx(classes.tab, tabClassName)}
          disabled={disabled}
          isLast
          isSelected={id === timeframe}
          key={id}
          label={getTabLabel(id)}
          onClick={onSelect}
          size={size}
        />
      ))}
    </div>
  );
};
